import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@microservices/wiskey-react-components'
import { Code as CodeIcon } from '@mui/icons-material'
import { Box, FormHelperText, Grid, Typography } from '@mui/material'

import { ScriptValueDialog } from '@components/ScriptValueDialog/ScriptValueDialog'

const PREVIEW_CHARS = 200

type Props = {
  scriptValue: string
  dialogTitle?: string
  buttonLabel?: string
  errorText?: string
  onSaveScript?: (script: string) => void
}

export const HtmlTemplateEditorField: FC<Props> = ({
  scriptValue,
  dialogTitle,
  buttonLabel,
  errorText,
  onSaveScript,
}) => {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const hasError = Boolean(errorText?.trim())
  const label = buttonLabel ?? t('printForms.templateEditor.editTemplate')

  const handleOpenDialog = () => setIsDialogOpen(true)

  const handleCloseDialog = () => setIsDialogOpen(false)

  const handleApplyScript = (next: string) => {
    onSaveScript?.(next)
  }

  const subtitle = t('printForms.templateEditor.handlebarsSubtitle')

  const defaultTitle = `${t('printForms.templateSection')} (${subtitle})`

  const previewSnippet =
    scriptValue.length > PREVIEW_CHARS ? `${scriptValue.slice(0, PREVIEW_CHARS)}…` : scriptValue

  return (
    <Grid container display={'flex'} flexDirection={'column'} gap={1} mb={1}>
      <Button
        fullWidth
        color={hasError ? 'error' : undefined}
        startIcon={<CodeIcon />}
        variant='outlined'
        onClick={handleOpenDialog}
      >
        {label}
      </Button>
      {hasError && (
        <FormHelperText error sx={{ mx: 0 }}>
          {errorText}
        </FormHelperText>
      )}
      {scriptValue.trim().length > 0 && (
        <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, maxHeight: 160, overflow: 'auto', p: 1 }}>
          <Typography
            component='pre'
            variant='caption'
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              margin: 0,
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {previewSnippet}
          </Typography>
        </Box>
      )}
      <ScriptValueDialog
        noValidate
        btnText={t('modal.applyBtnText')}
        isJsx={false}
        isShow={isDialogOpen}
        language='handlebars'
        title={dialogTitle ?? defaultTitle}
        value={scriptValue}
        windowBoundaries={false}
        onClose={handleCloseDialog}
        onSave={handleApplyScript}
      />
    </Grid>
  )
}
